import type { UUID } from "../../utils/uuid";
import type { Asset } from "./server/digital-asset-service";
import type { Folder } from "./server/folder-service";

export const constructCloudflareStreamUrl = (cloudflareStreamUid: string | null): string => {
  return `https://customer-v3ob3l120jo5wz6l.cloudflarestream.com/${cloudflareStreamUid}`;
};

export interface TreeViewItem {
  id: string;
  label: string;
  folderId: UUID | null;
  children?: TreeViewItem[];
}

export const ROOT_ID = "0";
const ROOT_LABEL = "Content Library";

export type FolderId = Asset["folder_id"] | typeof ROOT_ID;
export type UiFolderId = UUID | typeof ROOT_ID;

export const folderNameFromId = (folderId: FolderId, folders: Folder[] | null): string | null => {
  if (folderId === ROOT_ID) return ROOT_LABEL;
  if (!folders) return null;

  const folder = folders.find((f) => f.id === folderId);
  return folder?.name ?? null;
};

type FolderOption = {
  id: UiFolderId;
  label: string;
  depth: number;
};

export const flattenTreeItems = (items: TreeViewItem[], depth = 0): FolderOption[] =>
  items.flatMap((item) => [
    { id: item.id as UiFolderId, label: item.label, depth },
    ...(item.children?.length ? flattenTreeItems(item.children, depth + 1) : []),
  ]);

// Transforms a flat array of folders into a hierarchical tree structure
// compatible with MUI RichTreeView's `items` prop.
export const transformFoldersToTreeItems = (folders: Folder[]): TreeViewItem[] => {
  // Group folders by their parent_folder_id
  const childrenMap = new Map<UUID | null, Folder[]>();
  for (const folder of folders) {
    const parentId = folder.parent_folder_id;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(folder);
  }

  const buildTreeItems = (parentId: UUID | null): TreeViewItem[] => {
    const children = childrenMap.get(parentId) || [];

    // Sort children alphabetically by name for consistent ordering
    children.sort((a, b) => a.name.localeCompare(b.name));

    return children.map((folder): TreeViewItem => {
      const nestedChildren = buildTreeItems(folder.id);

      const item: TreeViewItem = {
        id: folder.id,
        label: folder.name,
        folderId: folder.id,
      };

      if (nestedChildren.length > 0) {
        item.children = nestedChildren;
      }

      return item;
    });
  };

  // Start building from root folders (parent_folder_id is null)
  const rootChildren = buildTreeItems(null);

  return [
    {
      id: ROOT_ID,
      label: ROOT_LABEL,
      folderId: null,
      children: rootChildren.length > 0 ? rootChildren : undefined,
    },
  ];
};

export const constructBreadcrumbs = (
  treeItems: TreeViewItem[],
  selectedTreeItemId: string | null,
): TreeViewItem[] => {
  if (!selectedTreeItemId) return [{ id: ROOT_ID, label: ROOT_LABEL, folderId: null }];

  const findPath = (
    items: TreeViewItem[],
    targetId: string,
    path: TreeViewItem[],
  ): TreeViewItem[] | null => {
    for (const item of items) {
      const currentPath = [...path, item];

      if (item.id === targetId) {
        return currentPath;
      }

      if (item.children?.length) {
        const result = findPath(item.children, targetId, currentPath);
        if (result) return result;
      }
    }
    return null;
  };

  // If treeItems is empty, return a default root breadcrumb
  if (treeItems.length === 0) {
    return [{ id: ROOT_ID, label: ROOT_LABEL, folderId: null }];
  }

  const breadcrumbs = findPath(treeItems, selectedTreeItemId, []);

  // If no path found but we have a selectedTreeItemId, return default root
  if (!breadcrumbs) {
    return [{ id: ROOT_ID, label: ROOT_LABEL, folderId: null }];
  }

  return breadcrumbs;
};
